/**
 * Extra preview detail line for accounting fix / repair script panels.
 */
export function accountingPreviewDetailLine(preview) {
  if (!preview?.status) {
    return null;
  }

  if (preview.no_sales_in_period) {
    return 'No gas sales in this date range — widen From / To dates or use the Fix button on a gas finding from Check accounts.';
  }

  if (preview.nothing_to_do) {
    return null;
  }

  if (preview.fix_type === 'gas_cylinder_ledger_repair') {
    const sales = preview.sales_repairable ?? 0;
    const rows = preview.ledger_rows_to_change ?? preview.rows_to_change ?? 0;
    if (sales > 0 || rows > 0) {
      return `${sales} gas sale(s), ${rows} ledger row(s) will be updated. See table below for before/after values.`;
    }
    return null;
  }

  if (preview.fix_type?.startsWith('wac_recalc_apply') && preview.stats) {
    const sales = preview.stats.sales_rows_with_changes ?? 0;
    const purchases = preview.stats.purchase_rows_with_changes ?? 0;
    const plan = preview.apply_plan;
    const planHint = plan?.window_count > 1
      ? ` Will run in ${plan.window_count} date windows — you will see progress for each window during apply.`
      : (plan?.phases?.length
        ? ` Apply runs in ${plan.phases.length} steps — progress is shown live.`
        : '');
    if (preview.fix_type === 'wac_recalc_apply_sales' && preview.rows_to_change > 0) {
      return `${preview.rows_to_change} fuel sale/recoup row(s) will be updated (${sales} sales, ${purchases} purchase rows in full replay). Tank purchase records stay the same.${planHint}`;
    }
    if (preview.fix_type === 'wac_recalc_apply_purchase' && preview.rows_to_change > 0) {
      return `${preview.rows_to_change} tank purchase row(s) will be updated. Sales costs stay the same.${planHint}`;
    }
    if (preview.rows_to_change > 0) {
      return `${preview.rows_to_change} row(s) will be updated — sales: ${sales}, purchases: ${purchases}.${planHint}`;
    }
  }

  if (preview.fix_type === 'ledger_trans_id_collision_split' && (preview.rows_to_change ?? 0) > 0) {
    return `${preview.rows_to_change} fuel purchase(s) will get a new transaction id. Debit and credit amounts stay the same.`;
  }

  if (preview.fix_type === 'nf_sale_gst_ledger_sync' && (preview.rows_to_change ?? 0) > 0) {
    const total = preview.change_records_total ?? preview.rows_to_change;
    const shown = preview.change_records?.length ?? 0;
    if (preview.change_records_truncated && shown > 0) {
      return `Showing ${shown} of ${total} sale(s) below — each row lists current value, new value, and the GST formula.`;
    }
    return `${preview.rows_to_change} sale(s) will be updated. See before/after values and formula below.`;
  }

  if (preview.rows_to_change > 0) {
    return `${preview.rows_to_change} row${preview.rows_to_change === 1 ? '' : 's'} will be updated.`;
  }

  return null;
}

export function accountingPreviewAlertClass(preview) {
  if (!preview?.status) {
    return 'info';
  }
  if (preview.manual_only) {
    return 'info';
  }
  if (preview.no_sales_in_period) {
    return 'warning';
  }
  if (preview.nothing_to_do) {
    return 'info';
  }
  if ((preview.rows_to_change ?? 0) > 0) {
    return 'warning';
  }
  return 'info';
}

export function shouldBlockRepairScriptRun(preview, fixType) {
  if (!preview?.status) {
    return false;
  }

  if (fixType === 'nf_sale_gst_ledger_sync' && (preview.rows_to_change ?? 0) === 0) {
    return true;
  }

  if (fixType === 'gas_cylinder_ledger_repair' && (preview.nothing_to_do || preview.no_sales_in_period)) {
    return true;
  }

  if (fixType?.startsWith('wac_recalc_apply') && (preview.rows_to_change ?? 0) === 0) {
    return true;
  }

  if (fixType === 'ledger_trans_id_collision_split' && (preview.rows_to_change ?? 0) === 0) {
    return true;
  }

  return false;
}
